import { getVisitCategories } from './categoryUtils'

const DATE_RANGE = {
  TODAY: 'today',
  WEEK: 'week',
  MONTH: 'month',
  LAST_MONTH: 'lastMonth',
  SPECIFIC: 'specific',
  OLDER: 'older'
}

export function useVisitFilters() {

  function getDateRanges(icons) {
    return [
      { label: 'ppv.date_range.today', value: DATE_RANGE.TODAY },
      { label: 'ppv.date_range.this_week', value: DATE_RANGE.WEEK },
      { label: 'ppv.date_range.this_month', value: DATE_RANGE.MONTH },
      { label: 'ppv.date_range.last_month', value: DATE_RANGE.LAST_MONTH },
      {
        label: 'ppv.date_range_modal.title',
        value: DATE_RANGE.SPECIFIC,
        icon: icons.mdiCalendarMonth
      },
      // TODO: Re-enable when the online data pull is available.
      // {
      //   label: 'ppv.older_visits.title',
      //   value: DATE_RANGE.OLDER,
      //   icon: icons.mdiClockOutline
      // }
    ]
  }

  function getTrackOptions(icons) {
    return [
      { value: 'newborn', label: 'ppv.track.newborn', icon: icons.mdiBabyFaceOutline },
      { value: 'child', label: 'ppv.track.child', icon: icons.mdiEmoticonHappyOutline },
      { value: 'youth', label: 'ppv.track.youth', icon: icons.mdiSchoolOutline },
      { value: 'adult', label: 'ppv.track.adult', icon: icons.mdiAccount },
      { value: 'antenatal', label: 'ppv.track.antenatal', icon: icons.mdiHumanPregnant },
      { value: 'postnatal', label: 'ppv.track.postnatal', icon: icons.mdiHeart }
    ]
  }

  function getTriageOptions(icons) {
    return [
      { value: 'immediate', label: 'ppv.triage.immediate', icon: icons.mdiAlertCircle },
      { value: 'urgent', label: 'ppv.triage.urgent', icon: icons.mdiAlert },
    ]
  }

  function getTrackMap(options) {
    const map = {}

    options.forEach(option => {
      map[option.value] = option.label
    })

    return map
  }

  function isInDateRange(dateStr, filters) {
    if (!dateStr) {
      return false
    }

    const date = new Date(dateStr)
    const now = new Date()
    const range = filters.dateRange

    if (range === DATE_RANGE.TODAY) {
      return date.toDateString() === now.toDateString()
    }

    if (range === DATE_RANGE.WEEK) {
      const startOfWeek = new Date(now)
      startOfWeek.setDate(now.getDate() - now.getDay())
      startOfWeek.setHours(0, 0, 0, 0)

      return date >= startOfWeek
    }

    if (range === DATE_RANGE.MONTH) {
      return (
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      )
    }

    if (range === DATE_RANGE.LAST_MONTH) {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)

      return (
        date.getMonth() === lastMonth.getMonth() &&
        date.getFullYear() === lastMonth.getFullYear()
      )
    }

    if (range === DATE_RANGE.SPECIFIC) {
      const from = filters.dateFrom ? new Date(filters.dateFrom) : null
      const to = filters.dateTo ? new Date(filters.dateTo) : null

      if (from) {
        from.setHours(0, 0, 0, 0)

        if (date < from) {
          return false
        }
      }

      if (to) {
        to.setHours(23, 59, 59, 999)

        if (date > to) {
          return false
        }
      }

      return true
    }

    if (range === DATE_RANGE.OLDER) {
      const threeMonthsAgo = new Date(
        now.getFullYear(),
        now.getMonth() - 3,
        1
      )

      return date < threeMonthsAgo
    }

    return true
  }

  function filterConditionsByMode(visit, conditionMode) {
    if (conditionMode !== 'user_selected') {
      return visit
    }

    const conditions = visit.conditions || []

    if (!conditions.length) {
      return visit
    }

    const filteredConditions = conditions.filter((c) => c.selectedByUser === true)

    return {
      ...visit,
      conditions: filteredConditions,
    }
  }

  function applyFilters(allVisits, filters) {
    const trackSet = new Set(filters.tracks || [])
    const diseaseSet = new Set(filters.diseaseCategories || [])
    const triageSet = new Set(filters.triages || [])
    const conditionMode = filters.conditionMode || 'user_selected'

    return allVisits
      .map((visit) => filterConditionsByMode(visit, conditionMode))
      .filter(visit => {
        const dateVal = visit.createdAt || visit.date

        if (!isInDateRange(dateVal, filters)) {
          return false
        }

        if (trackSet.size > 0 && !trackSet.has(visit.patientType)) {
          return false
        }

        if (triageSet.size > 0) {
          const hasMatchingTriage = visit.conditions?.some(
            c => triageSet.has(c.triage_recommendation)
          )

          if (!hasMatchingTriage) {
            return false
          }
        }

        if (diseaseSet.size > 0) {
          const visitCategories = getVisitCategories(visit)

          if (!visitCategories.some(c => diseaseSet.has(c))) {
            return false
          }
        }

        if (conditionMode === 'user_selected' && (!visit.conditions || !visit.conditions.length)) {
          return false
        }

        return true
      })
      .map(visit => {
        if (triageSet.size === 0) {
          return visit
        }

        return {
          ...visit,
          conditions: visit.conditions?.filter(
            c => triageSet.has(c.triage_recommendation)
          ) || []
        }
      })
  }

  return {
    getDateRanges,
    getTrackOptions,
    getTriageOptions,
    getTrackMap,
    isInDateRange,
    applyFilters
  }
}
